import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config.json";

const ADMIN_API_URL = config.ADMIN_API_URL;

/**
 * @typedef {object} BreakdownFactor
 * @property {number} id
 * @property {"Scope 1"|"Scope 2"|"Scope 3"} scope
 * @property {string} country_code
 * @property {number|null} unit_id
 * @property {string} unit
 * @property {number|null} ghg_database_id
 * @property {string|null} ghg_database_name
 * @property {"DIRECT"|"FUEL_ENERGY"} calculation_method
 * @property {"GAS_MASS"|"CO2E"} ef_basis
 * @property {number|null} density
 * @property {number|null} ncv
 * @property {number|null} ef_co2
 * @property {number|null} ef_ch4
 * @property {number|null} ef_n2o
 * @property {number|null} gwp_co2
 * @property {number|null} gwp_ch4
 * @property {number|null} gwp_n2o
 * @property {string|null} gwp_source
 * @property {number|null} emission_factor kg CO2e per unit
 * @property {boolean} has_gas_breakdown
 * @property {number|null} energy_tj_per_unit
 * @property {{ co2: number|null, ch4: number|null, n2o: number|null }} co2e_per_unit_by_gas
 */

/**
 * @typedef {object} BreakdownKpi
 * @property {number} kpi_id
 * @property {string} kpi_title
 * @property {number|null} module_id
 * @property {BreakdownFactor[]} factors
 */

/**
 * @typedef {object} BreakdownFilters
 * @property {string} search
 * @property {string} scope
 * @property {string} databaseId
 * @property {string} method
 */

/**
 * @typedef {object} BreakdownPage
 * @property {BreakdownKpi[]} kpis
 * @property {number} total_kpis every KPI the filters match
 * @property {{ factors: number, gas_wise: number, fuel_energy: number }} totals over every match, not just this page
 * @property {number} limit
 * @property {number} offset
 */

/**
 * One page of factors grouped by the KPI they price, each with its gas columns
 * and the kg CO2e per unit each gas contributes, plus the totals of every
 * match. Blank filters are left out, and the rest are encoded here because
 * apiCall joins GET params as they are. Silent, because the page shows its
 * own error.
 *
 * @param {BreakdownFilters} filters
 * @param {{ limit: number, offset: number }} page
 * @returns {Promise<{ isSuccess: boolean, data: { data?: BreakdownPage, error?: string } } | undefined>}
 */
export const getEmissionFactorBreakdown = ({ search, scope, databaseId, method }, { limit, offset }) =>
  apiCall(
    `${ADMIN_API_URL}getEmissionFactorBreakdown`,
    {},
    {
      ...Object.fromEntries(
        Object.entries({ search, scope, ghg_database_id: databaseId, calculation_method: method })
          .filter(([, value]) => value)
          .map(([key, value]) => [key, encodeURIComponent(value)]),
      ),
      limit,
      offset,
    },
    "GET",
    { silent: true },
  );
